<template>
    <div class="vehicle-images">
        <!-- Desktop main view -->
        <template v-if="isLargeScreen">
            <div :class="mainImageClasses" @click.prevent="toggleCarouselGallery(0)">
                <picture>
                    <source v-if="mainImage.webpSrc" :srcset="mainImage.webpSrc" type="image/webp" />
                    <source :srcset="mainImage.src" type="image/jpeg" />

                    <img :src="mainImage.src" :alt="$t('vehicle_images.common_alt')" fetchpriority="high" />
                </picture>
                <VehicleIconBar empty-heart-icon="heart_black_outline" circled-icons :vehicle="vehicle" />
            </div>
            <div v-if="sideImages.length > 0" class="vehicle-images__side-images">
                <div v-for="(image, index) in sideImages" :key="index" :class="sideImageWrapperClasses">
                    <div class="vehicle-images__side-image" @click.prevent="toggleCarouselGallery(index + 1)">
                        <picture>
                            <source v-if="image.webpSrc" :srcset="image.webpSrc" type="image/webp" />
                            <source :srcset="image.src" type="image/jpeg" />

                            <img :src="image.src" :alt="$t('vehicle_images.common_alt')" fetchpriority="high" />
                        </picture>
                    </div>
                    <div v-if="index === sideImages.length - 1" class="vehicle-images__button-overlay">
                        <button class="button button--primary" @click="toggleCarouselGallery(0)">
                            {{
                                $tc('sections.image_section.more_images', additionalImagesCount, {
                                    imageCount: additionalImagesCount,
                                })
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <!-- Mobile main view -->
        <div v-else class="vehicle-images__mobile-wrapper">
            <VehicleIconBar empty-heart-icon="heart_black_outline" circled-icons :vehicle="vehicle" />
            <VehicleImageCarousel
                :images="vehicle.images"
                class="vehicle-images__mobile-carousel"
                @slide-click="i => toggleCarouselGallery(i)"
            />
        </div>

        <!-- Fullscreen carousel gallery -->
        <VehicleImageCarousel
            v-if="carouselGalleryVisible"
            :images="vehicle.images"
            :fullscreen="true"
            :use-keyboard="true"
            :index-of-clicked-image="indexOfClickedImage"
            @toggle-carousel="toggleCarouselGallery"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VehicleIconBar from '~/components/vehicles/VehicleIconBar.vue';
const MAX_MAIN_IMAGES = 5;

export default {
    components: {
        VehicleIconBar,
        VehicleImageCarousel: () => import('~/components/vehicles/VehicleImageCarousel.vue'),
    },
    props: {
        vehicle: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            carouselGalleryVisible: false,
            indexOfClickedImage: 0,
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen']),
        mainImageClasses() {
            return {
                'vehicle-images__main-image': true,
                'vehicle-images__main-image--large': this.mainImagesCount === 1,
            };
        },
        sideImageWrapperClasses() {
            return {
                'vehicle-images__side-image-wrapper': true,
                'vehicle-images__side-image-wrapper--medium': this.mainImagesCount === 3,
                'vehicle-images__side-image-wrapper--large': this.mainImagesCount === 2,
            };
        },
        mainImagesCount() {
            if (this.mainViewImages.length >= MAX_MAIN_IMAGES) {
                return MAX_MAIN_IMAGES;
            } else if (this.mainViewImages.length === 4) {
                return 3;
            } else {
                return this.mainViewImages.length;
            }
        },
        mainImage() {
            return (this.mainViewImages && this.mainViewImages[0]) || {};
        },
        mainViewImages() {
            let images = [...this.vehicle.images];
            images = images.slice(0, MAX_MAIN_IMAGES);

            return images.map((image, index) => {
                const { sizes, webpSizes } = image;

                if (index === 0) {
                    return { src: sizes.large, webpSrc: webpSizes && webpSizes.large };
                } else {
                    return { src: sizes.medium, webpSrc: webpSizes && webpSizes.medium };
                }
            });
        },
        sideImages() {
            return this.mainViewImages.slice(1, this.mainImagesCount);
        },
        additionalImagesCount() {
            return this.vehicle.images.length - this.mainImagesCount;
        },
    },
    methods: {
        toggleCarouselGallery(i) {
            this.indexOfClickedImage = i;
            this.carouselGalleryVisible = !this.carouselGalleryVisible;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

$img-spacing: 6px;
$halved-img-spacing: calc($img-spacing / 2);

.vehicle-images {
    display: flex;
    width: 100%;
    max-height: 475px;
    min-height: 260px;
    position: relative;
    -webkit-tap-highlight-color: transparent;

    @include lg {
        min-height: 475px;
    }

    &__main-image {
        position: relative;
        width: 62.5%;
        height: 100%;
        cursor: pointer;

        &--large {
            height: 26rem;
            width: 100%;
        }

        img {
            object-fit: cover;
            width: 100%;
            height: 100%;
        }
    }

    &__side-images {
        display: flex;
        justify-content: space-between;
        align-content: space-between;
        flex-wrap: wrap;
        width: 37.5%;
        padding: 0 0 0 $img-spacing;
    }

    &__side-image-wrapper {
        position: relative;
        width: calc(50% - #{$halved-img-spacing});
        height: calc(50% - #{$halved-img-spacing});

        &--medium {
            width: 100%;
            height: calc(50% - #{$halved-img-spacing});
        }

        &--large {
            width: 100%;
            height: 100%;
        }
    }

    &__side-image {
        cursor: pointer;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: 100%;
        }
    }

    &__button-overlay {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__mobile-wrapper {
        width: 100%;
        height: 100%;

        @include lg {
            display: none;
        }
    }

    &__side-images,
    &__main-image {
        @media screen and (max-width: $bp-lg-min) {
            display: none;
        }
    }

    &__mobile-carousel {
        height: 16.25rem;
        width: 100vw;
        overflow: visible;
        left: 50%;
        right: 50%;
        margin-left: -50vw;
        margin-right: -50vw;
    }
}
</style>
